<template>
  <div class="bg-background text-foreground">

    <!-- Hero -->
    <section class="relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background pointer-events-none"></div>
      <div class="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-primary/5 blur-3xl pointer-events-none"></div>

      <div class="relative max-w-3xl mx-auto px-6 pt-16 pb-10 sm:pt-24 sm:pb-12 text-center">
        <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6 border border-primary/20">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
          Built for OnlineJobs.ph Freelancers
        </div>

        <h1 class="text-4xl sm:text-5xl lg:text-[3.1rem] font-extrabold text-foreground leading-[1.08] tracking-tight mb-6">
          The AI-powered way to apply on <span class="text-primary">OnlineJobs.ph</span>.
        </h1>

        <p class="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto">
          Link your OnlineJobs.ph account and let Applypa apply for you — a tailored AI cover letter for every job, instant alerts the moment a new listing goes live, and everything tracked in one dashboard.
        </p>

        <div class="flex flex-wrap gap-3 justify-center mb-5">
          <router-link
            to="/register"
            class="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-base font-semibold hover:bg-primary/90 transition-colors shadow-sm"
          >
            Start Your Free 14-Day Trial →
          </router-link>
          <a
            href="#autoApply"
            class="inline-flex items-center gap-2 bg-background text-foreground border border-border rounded-lg px-6 py-3 text-base font-semibold hover:bg-muted/50 transition-colors"
          >
            See How Auto Apply Works
          </a>
        </div>

        <div class="flex flex-wrap gap-x-5 gap-y-1.5 justify-center text-sm text-muted-foreground mb-6">
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> 14-day free trial, no credit card required</span>
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> OnlineJobs.ph credentials stored encrypted</span>
        </div>

      </div>

      <div class="relative max-w-3xl mx-auto px-6 pb-8">
        <div class="pt-8 border-t border-border">
          <div class="text-xs font-semibold text-muted-foreground text-center uppercase tracking-widest mb-4">Also browse jobs from</div>
          <div class="flex flex-wrap justify-center gap-2">
            <span v-for="p in otherPlatforms" :key="p" class="bg-muted text-foreground/80 text-sm font-medium px-3.5 py-1.5 rounded-full border border-border">{{ p }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Trusted by Filipino freelancers (stats) -->
    <section class="bg-background">
      <div class="max-w-4xl mx-auto px-6 py-12 sm:py-14">
        <div class="text-xs font-bold text-primary text-center uppercase tracking-widest mb-6">Trusted by OnlineJobs.ph Freelancers</div>
        <div class="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          <div class="text-center py-4 sm:py-0">
            <div class="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">{{ stat1Display }}</div>
            <div class="text-sm text-muted-foreground mt-2">Filipino Freelancers</div>
          </div>
          <div class="text-center py-4 sm:py-0">
            <div class="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">{{ stat2Display }}</div>
            <div class="text-sm text-muted-foreground mt-2">Jobs Indexed</div>
          </div>
          <div class="text-center py-4 sm:py-0">
            <div class="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">{{ stat3Display }}</div>
            <div class="text-sm text-muted-foreground mt-2">AI Cover Letters Generated</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Auto Apply — led with first since it's the OnlineJobs.ph-exclusive hook for this
         audience. Fixed dark colors on purpose (not theme tokens): this band is meant to
         always read as dark/bold, regardless of the site's light/dark mode — using
         bg-primary here would flip to a near-white background in dark mode since
         --primary inverts per theme. -->
    <section id="autoApply" class="py-16 sm:py-24 px-6 bg-black text-white scroll-mt-16">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col md:flex-row items-center gap-10 md:gap-14">
          <div class="w-full md:w-[42%]">
            <div class="flex flex-wrap items-center gap-2 mb-4">
              <div class="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full"><i class="fas fa-bolt"></i> Auto Apply</div>
              <div class="inline-flex items-center gap-1.5 bg-amber-400/20 text-amber-100 text-xs font-bold px-3 py-1.5 rounded-full border border-amber-300/40"><i class="fas fa-circle-info"></i> Built for OnlineJobs.ph</div>
            </div>
            <h2 class="text-3xl sm:text-4xl font-extrabold mb-3 leading-tight tracking-tight">You're already on OnlineJobs.ph. Let Applypa apply for you.</h2>
            <p class="text-sm sm:text-base leading-relaxed mb-6 text-white/80">Link the OnlineJobs.ph account you already have, and Applypa writes a tailored AI cover letter and submits a real application to OnlineJobs.ph listings automatically — at the pace you set. Other connected platforms are browse, filter, and alert only.</p>
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-link text-white text-sm"></i></div>
                <div>
                  <p class="text-sm font-bold text-white">Works with your existing OnlineJobs.ph login</p>
                  <p class="text-sm mt-0.5 text-white/60">No new profile to build — Applypa uses the OnlineJobs.ph account you already have.</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-gear text-white text-sm"></i></div>
                <div>
                  <p class="text-sm font-bold text-white">Runs automatically, not one-by-one</p>
                  <p class="text-sm mt-0.5 text-white/60">Once it's on, applications go out to matching OnlineJobs.ph listings on your schedule without a manual approval step for each one.</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-hand text-white text-sm"></i></div>
                <div>
                  <p class="text-sm font-bold text-white">Skip before it sends, or turn it off</p>
                  <p class="text-sm mt-0.5 text-white/60">See every queued application before its send time and skip it, or turn automation off completely, any time.</p>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full md:w-[58%]">
            <div class="relative rounded-3xl border border-white/15 bg-white/5 p-4 sm:p-6 overflow-hidden">
              <img :src="automationApplyImg" alt="Auto Apply automation connected to OnlineJobs.ph" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Beyond OnlineJobs.ph -->
    <section id="howItWorks" class="py-16 sm:py-24 px-6 bg-card scroll-mt-16">
      <div class="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-14">
        <div class="w-full md:w-[58%]">
          <div class="relative rounded-3xl border border-border bg-gradient-to-br from-muted to-background p-4 sm:p-6 overflow-hidden">
            <div class="absolute -top-16 -right-16 w-56 h-56 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <img :src="jobFiltersImg" alt="Applypa unified job search across multiple platforms" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-[42%]">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-layer-group"></i> Beyond OnlineJobs.ph</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">See more jobs without leaving your workflow.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Applypa layers on top of OnlineJobs.ph and also pulls in JobStreet, Cloudstaff, Emapta, BossJob, Kalibrr, and VAWorkers PH — so when OnlineJobs.ph is quiet, you've still got more places to look, all from the same dashboard.</p>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-layer-group text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">All your job platforms, one dashboard</p>
                <p class="text-sm text-muted-foreground mt-0.5">OnlineJobs.ph, JobStreet, Cloudstaff, Emapta, BossJob, Kalibrr, and VAWorkers PH — in one place.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-sliders text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Only see jobs worth applying to</p>
                <p class="text-sm text-muted-foreground mt-0.5">Filter by keyword, job type, salary, and category — cut the noise before it reaches you.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-bookmark text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Save multiple filter presets</p>
                <p class="text-sm text-muted-foreground mt-0.5">Switch between saved searches instantly — no re-entering criteria every time.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- AI Cover Letters -->
    <section id="aiCoverLetter" class="py-16 sm:py-24 px-6 bg-background scroll-mt-16">
      <div class="max-w-6xl mx-auto flex flex-col md:flex-row-reverse items-center gap-10 md:gap-14">
        <div class="w-full md:w-[43%]">
          <div class="relative rounded-3xl border border-border bg-gradient-to-br from-muted to-background p-3 sm:p-5 overflow-hidden flex justify-center">
            <div class="absolute -top-16 -left-16 w-56 h-56 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <img :src="aiCoverLetterImg" alt="AI cover letter generator" class="relative w-auto h-auto max-h-[760px] drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-[57%]">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-file-alt"></i> AI Cover Letters</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">Write personalized OnlineJobs.ph cover letters in seconds.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Stop writing the same letter over and over. Applypa reads the OnlineJobs.ph job post and drafts a personalized one for you — instantly.</p>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-wand-magic-sparkles text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">One-click generation</p>
                <p class="text-sm text-muted-foreground mt-0.5">Open any supported job post and hit "Generate with AI" — ready in seconds.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-user-pen text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Sounds like you, not a template</p>
                <p class="text-sm text-muted-foreground mt-0.5">Written from your resume and the specific job description — not a generic template.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-pen-to-square text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Matches your skills to each job's requirements</p>
                <p class="text-sm text-muted-foreground mt-0.5">Reads what the job post is actually asking for and highlights how your experience fits.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Instant Alerts -->
    <section id="notifications" class="py-16 sm:py-24 px-6 bg-card scroll-mt-16">
      <div class="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-14">
        <div class="w-full md:w-[58%]">
          <div class="relative rounded-3xl border border-border bg-gradient-to-br from-muted to-background p-4 sm:p-6 overflow-hidden">
            <div class="absolute -top-16 -right-16 w-56 h-56 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <img :src="notificationsImg" alt="Telegram, Discord, and email notifications" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-[42%]">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-bell"></i> Instant Alerts</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">Be one of the first applicants on OnlineJobs.ph.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">New OnlineJobs.ph listings often get dozens of applicants within the first hour. Applypa notifies you the moment a matching job is posted.</p>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fab fa-telegram text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Telegram, Discord &amp; email alerts</p>
                <p class="text-sm text-muted-foreground mt-0.5">Get notified on Telegram, Discord, or email the instant a matching job posts.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-bullseye text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Never miss a matching job</p>
                <p class="text-sm text-muted-foreground mt-0.5">Alerts are tied to your saved filters — no noise.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-wifi text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Works while you're offline</p>
                <p class="text-sm text-muted-foreground mt-0.5">Applypa monitors OnlineJobs.ph and your other connected platforms around the clock.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison -->
    <section class="py-16 sm:py-24 px-6 bg-card">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">The Difference</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Applying on OnlineJobs.ph the old way vs. with Applypa</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-lg mx-auto">Same job hunt. A fraction of the effort.</p>

      <div class="max-w-3xl mx-auto grid sm:grid-cols-2 gap-4">
        <div class="rounded-2xl border border-border bg-muted/40 p-6">
          <div class="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">Without Applypa</div>
          <ul class="space-y-3">
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Refresh OnlineJobs.ph all day</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Copy-paste cover letters</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Miss new postings overnight</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Apply one job at a time</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Lose out to faster applicants</li>
          </ul>
        </div>
        <div class="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <div class="text-sm font-bold text-primary uppercase tracking-wider mb-4">With Applypa</div>
          <ul class="space-y-3">
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> Auto Apply to OnlineJobs.ph</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> AI cover letters</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> Instant alerts</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> More platforms in one dashboard</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> Save hours every week</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Trust strip -->
    <section class="py-12 px-6 bg-muted border-y border-border">
      <div class="max-w-4xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
        <div class="flex flex-col items-center gap-2">
          <i class="fas fa-lock text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">Your OnlineJobs.ph login, encrypted</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">Your OnlineJobs.ph credentials are encrypted and never shared with third parties.</p>
        </div>
        <div class="flex flex-col items-center gap-2">
          <i class="fas fa-hand-holding-dollar text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">14-Day Free Trial</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">Full Premium access for 14 days, no credit card required.</p>
        </div>
        <div class="flex flex-col items-center gap-2">
          <i class="fas fa-users text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">Built with real Filipino freelancers</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">Shaped by feedback from the people who use it every day.</p>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="py-16 sm:py-24 px-6 bg-background">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">Real Users</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">What freelancers are saying</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-sm mx-auto">From real users — emails, comments, and messages.</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">

        <div v-for="(t, index) in testimonials" :key="index" class="bg-card rounded-2xl p-6 border border-border shadow-sm flex flex-col">
          <div class="flex gap-1 mb-3">
            <i class="fas fa-star text-yellow-400 text-sm" v-for="n in 5" :key="'t' + index + n"></i>
          </div>
          <p class="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">"{{ t.quote }}"
            <span v-if="t.translation" class="block mt-1 text-xs text-muted-foreground italic">"{{ t.translation }}"</span>
          </p>
          <div class="text-xs font-semibold text-muted-foreground">— {{ t.name }}</div>
        </div>

      </div>
    </section>

    <!-- Pricing -->
    <section class="py-16 sm:py-24 px-6 bg-card">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">Pricing</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Try full Premium access for 14 days.</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-lg mx-auto">No credit card required. After that you keep a Free plan, or upgrade to Basic or Premium.</p>

      <div class="max-w-4xl mx-auto grid sm:grid-cols-2 gap-4">
        <div class="rounded-2xl border-2 border-primary bg-background p-6">
          <div class="text-sm font-bold text-primary uppercase tracking-wider mb-1">Basic</div>
          <div class="text-3xl font-extrabold text-foreground mb-4">{{ formatPeso(basicFeatures.price) }}<span class="text-sm font-medium text-muted-foreground">/mo</span></div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li v-for="bullet in basicBullets" :key="bullet">{{ bullet }}</li>
          </ul>
        </div>
        <div class="rounded-2xl border border-border bg-background p-6">
          <div class="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-1">Premium</div>
          <div class="text-3xl font-extrabold text-foreground mb-4">{{ formatPeso(premiumFeatures.price) }}<span class="text-sm font-medium text-muted-foreground">/mo</span></div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li v-for="bullet in premiumBullets" :key="bullet">{{ bullet }}</li>
          </ul>
        </div>
      </div>
      <div class="text-center mt-8">
        <router-link
          to="/subscription/plans"
          class="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-8 rounded-lg inline-block transition"
        >
          See All Plans
        </router-link>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-16 sm:py-24 px-6 bg-muted">
      <div class="max-w-2xl mx-auto">
        <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">FAQ</div>
        <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Common questions</h2>
        <p class="text-base text-muted-foreground text-center mb-10 max-w-sm mx-auto">Everything you need to know before getting started.</p>

        <div class="space-y-3">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="bg-card rounded-xl border border-border overflow-hidden"
          >
            <button
              @click="toggleFaq(index)"
              class="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none group"
            >
              <span class="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{{ faq.question }}</span>
              <i
                class="fas fa-chevron-down text-muted-foreground text-xs transition-transform duration-200 ml-4 shrink-0"
                :class="{ 'rotate-180': openFaq === index }"
              ></i>
            </button>
            <div v-show="openFaq === index" class="px-5 pb-5">
              <p class="text-sm text-muted-foreground leading-relaxed">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA — fixed dark colors (same reason as Auto Apply above): bg-primary would
         flip to a near-white band in dark mode since --primary inverts per theme. -->
    <section class="py-16 sm:py-24 px-6 bg-black text-white text-center">
      <h2 class="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight">Your next OnlineJobs.ph application starts here.</h2>
      <p class="text-base mb-4 max-w-md mx-auto text-white/85">
        Link your OnlineJobs.ph account and let Applypa apply while you sleep.
      </p>
      <p class="text-sm mb-8 text-white/60">
        14-day free trial · No credit card required
      </p>
      <div v-if="!isLoggedIn" class="flex flex-col items-center gap-3">
        <div class="flex flex-wrap gap-3 justify-center">
          <router-link to="/login" class="border-2 border-white/40 text-white rounded-xl px-7 py-3.5 text-base font-bold hover:bg-white/10 transition">
            Log In
          </router-link>
          <router-link to="/register" class="bg-white text-black rounded-xl px-7 py-3.5 text-base font-bold hover:opacity-90 transition">
            Start Your Free Trial →
          </router-link>
        </div>
      </div>
      <div v-else>
        <router-link to="/job-filters" class="bg-white text-black rounded-xl px-7 py-3.5 text-sm font-bold hover:opacity-90 transition inline-block">
          Browse Jobs →
        </router-link>
      </div>
    </section>

  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import authStore from '@/store/auth';
import { useGuestPreview } from '@/composables/useLandingVersion';
import apiClient from '@/services/api';
import {
  resolvePlanFeatures,
  formatPeso,
  coverLettersBullet,
  autoApplyBullet,
  directApplyBullet,
  useSubscriptionPlans,
} from '@/composables/useSubscriptionPlans';
import aiCoverLetterImg from '@/assets/applypa/ai-cover-letter-hero-onlinejobs.png';
import jobFiltersImg from '@/assets/applypa/job-filters-hero-onlinejobs.png';
import notificationsImg from '@/assets/applypa/notifications-hero-onlinejobs.png';
import automationApplyImg from '@/assets/applypa/auto-apply-hero-onlinejobs.png';

export default {
  name: 'HomepageOnlineJobs',
  setup() {
    const { isGuestPreview } = useGuestPreview();
    const isLoggedIn = authStore.state.isLoggedIn && !isGuestPreview.value;
    authStore.checkAuthStatus();

    // OnlineJobs.ph leads the hero strip separately (Auto Apply is OLJ-only) —
    // this list is everything else Applypa also aggregates.
    const otherPlatforms = ['JobStreet', 'Cloudstaff', 'Emapta', 'BossJob', 'Kalibrr', 'VAWorkers PH'];

    // Pricing teaser — pulled from the same subscription-plans API as
    // SubscriptionPlansPage.vue so the numbers here can't go stale.
    const { fetchSubscriptionPlans, findPlan } = useSubscriptionPlans();
    const basicFeatures = computed(() => resolvePlanFeatures(findPlan('basic', 30), 'basic'));
    const premiumFeatures = computed(() => resolvePlanFeatures(findPlan('premium', 30), 'premium'));

    const basicBullets = computed(() => [
      coverLettersBullet(basicFeatures.value),
      autoApplyBullet(basicFeatures.value),
      directApplyBullet(basicFeatures.value),
    ].filter(Boolean));
    const premiumBullets = computed(() => [
      coverLettersBullet(premiumFeatures.value),
      autoApplyBullet(premiumFeatures.value),
      premiumFeatures.value.has_priority_support ? 'Priority support' : directApplyBullet(premiumFeatures.value),
    ].filter(Boolean));

    const openFaq = ref(null);
    const toggleFaq = (index) => {
      openFaq.value = openFaq.value === index ? null : index;
    };

    // Real user testimonials — Taglish quotes with English translations where applicable. Do not fabricate.
    const testimonials = [
      { quote: 'Maganda app na yan. Automatic nagfofofollow up message after mo mag apply. Kaya may client na nagrereply talaga.', translation: "It's a good app. It automatically sends follow-up messages after you apply — that's why clients actually reply.", name: 'Samiya L.' },
      { quote: 'Im using Applypa, mabilis talaga to filter yung gusto mong job posting.', translation: "It's really fast to filter the job postings you want.", name: 'Kang X.' },
      { quote: "Thank you so much!! I'm so excited to use your platform. Kudos for this awesome innovation!! I use it almost every day. User interface is neat. And features are awesome.", translation: null, name: 'Joemae P.' },
      { quote: "I've already tried it — mas mabilis sya mkpg apply with free generate cover letter and smooth sya gmitin.", translation: "It's faster to apply with the free cover letter generator and smooth to use.", name: 'Anjeanette D.' },
      { quote: "Ma'am it works. Oh my goodness.", translation: null, name: 'JR E.' },
      { quote: "Anything is good and perfectly well working. I'm so amazed about the structure of this platform. Great job!", translation: null, name: 'Aleman J.' },
      { quote: 'UI/UX is great, kudos!', translation: null, name: 'Chauncey M.' },
    ];

    // FAQ answers accurately reflect: Auto Apply requires OnlineJobs.ph, runs on schedule with no
    // per-application approval, and the only user controls are skip-before-send and off.
    // OnlineJobs.ph-specific questions lead since this page targets that audience.
    const faqs = [
      {
        question: 'How does Auto Apply work with OnlineJobs.ph?',
        answer: 'Auto Apply requires a linked OnlineJobs.ph account. Once turned on, Applypa finds matching OnlineJobs.ph listings, writes a tailored AI cover letter, and submits the application automatically on the schedule you set. There\'s no per-application approval step while it\'s running.',
      },
      {
        question: 'Do I need an OnlineJobs.ph account to use Applypa?',
        answer: 'To use Auto Apply, yes — you\'ll need a linked OnlineJobs.ph account. You can still browse and manually apply on other supported platforms without one.',
      },
      {
        question: 'Can I stop an application before it\'s sent?',
        answer: 'Yes. Every queued Auto Apply application appears before its scheduled send time, and you can skip it. You can also turn automation off completely at any time.',
      },
      {
        question: 'Is my OnlineJobs.ph login safe?',
        answer: 'Yes. Your OnlineJobs.ph credentials are encrypted using industry-standard encryption before being stored, used only to fetch listings or submit applications on your behalf, and never shared with third parties.',
      },
      {
        question: 'Will employers know I used AI to write my cover letter?',
        answer: 'For manual applications, cover letters are generated from your resume and the specific job description, and you can review and edit before sending. Auto Apply sends its AI-generated letter automatically without a manual edit step — that\'s the tradeoff for running on autopilot.',
      },
      {
        question: 'Which other job platforms does Applypa support?',
        answer: 'Beyond OnlineJobs.ph, Applypa aggregates listings from JobStreet, Cloudstaff, Emapta, BossJob, Kalibrr, and VAWorkers PH into the same dashboard. Auto Apply currently works only with OnlineJobs.ph — the rest are for browsing, filtering, and alerts. LinkedIn, Indeed, and Upwork support is coming soon.',
      },
      {
        question: 'Is Applypa free to use?',
        answer: 'Applypa offers a 14-day free trial with full Premium access — no credit card required. After the trial, you move to the Free plan automatically: unlimited applying and 5 AI cover letters a month. Upgrade to Basic for 250 cover letters a month, or to Premium for 500 plus unlimited Auto Apply.',
      },
      {
        question: 'What kinds of jobs can I apply to?',
        answer: 'Any remote or freelance role from OnlineJobs.ph and our other supported platforms — virtual assistant, customer support, bookkeeping, social media management, data entry, web development, graphic design, and more. You control which job types and salary ranges show up in your filters.',
      },
      {
        question: 'How is Applypa different from applying manually on OnlineJobs.ph?',
        answer: 'Manual applying means writing a new cover letter for every job and refreshing OnlineJobs.ph to catch new postings. Applypa monitors listings across all your connected platforms, alerts you instantly, and generates a tailored cover letter from your resume — so you spend less time on repetitive work.',
      },
    ];

    // Baselines keep the section looking established while real counts are still ramping up.
    // Real API values only ever raise the displayed number, never lower it below the baseline.
    const STAT1_BASELINE = 850;
    const STAT2_BASELINE = 3200;
    const STAT3_BASELINE = 1100;

    const stat1 = ref(STAT1_BASELINE);
    const stat2 = ref(STAT2_BASELINE);
    const stat3 = ref(STAT3_BASELINE);

    const stat1Display = computed(() => stat1.value.toLocaleString() + '+');
    const stat2Display = computed(() => stat2.value.toLocaleString() + '+');
    const stat3Display = computed(() => stat3.value.toLocaleString() + '+');

    let statsInterval = null;

    const fetchStats = async () => {
      try {
        const res = await apiClient.get('/stats/public_stats/');
        if (res.data.freelancers != null) stat1.value = Math.max(STAT1_BASELINE, res.data.freelancers);
        if (res.data.jobs_processed != null) stat2.value = Math.max(STAT2_BASELINE, res.data.jobs_processed);
        if (res.data.cover_letters != null) stat3.value = Math.max(STAT3_BASELINE, res.data.cover_letters);
      } catch {
        // keep previous values on error
      }
    };

    onMounted(() => {
      fetchStats();
      fetchSubscriptionPlans();
      statsInterval = setInterval(fetchStats, 60000);
    });

    onBeforeUnmount(() => clearInterval(statsInterval));

    return {
      isLoggedIn,
      otherPlatforms,
      basicFeatures,
      premiumFeatures,
      basicBullets,
      premiumBullets,
      formatPeso,
      stat1Display,
      stat2Display,
      stat3Display,
      aiCoverLetterImg,
      jobFiltersImg,
      notificationsImg,
      automationApplyImg,
      testimonials,
      faqs,
      openFaq,
      toggleFaq,
    };
  },
};
</script>
